import {isEmpty} from 'lodash';
import {ellipsis} from 'polished';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useClipboard} from '../../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {LegacyInteractionParent} from '../../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {AnnotationDataTrackingEventPayload} from '../../../../../../services/tracking/annotation';
import {SelectionItem} from '../../../../../common/annotation/annotationsHighlightViewer';
import {FloatingToolbar} from '../../../../../common/floatingBars/floatingToolbar';
import {FloatingToolbarButton} from '../../../../../common/floatingBars/floatingToolbarButton';

const MAX_VISIBLE = 50;

interface SelectionShopControlsProps {
  toggleSelection: (selectionShopItem: SelectionItem) => void;
  selections: ReadonlyArray<SelectionItem>;
  clearAllSelections: () => void;
  selectedAnnotationsForDataTracking: ReadonlyArray<AnnotationDataTrackingEventPayload>;
  setCanShowCopyButton: (canShowCopyButton: boolean) => void;
}

const StyledWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: 500px;
  display: flex;
`;

const StyledItemsDiv = styled.div`
  padding-top: 1px;
  margin: 0 4px;
  display: flex;
  min-width: 0;
  gap: 4px;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
`;

const StyledItemSpan = styled.span`
  display: inline-block;
  border-radius: 2px;
  padding: 0 2px;
  color: ${(p) => p.theme.greys.shade50};
  border: 1px solid ${(p) => p.theme.greys.shade50};

  ${ellipsis('150px')};

  &:hover {
    color: ${(p) => p.theme.greys.shade30};
    border: 1px solid ${(p) => p.theme.greys.shade30};
  }
`;

// Change this flag to true to enable the selection shop controls in development.
const shouldRenderControls = false;

export const SelectionShopControls: FC<SelectionShopControlsProps> = ({
  selections,
  toggleSelection,
  clearAllSelections,
  selectedAnnotationsForDataTracking,
  setCanShowCopyButton,
}) => {
  const {copy} = useClipboard();
  const {trackDataEvent} = useDataEventTracking();

  const visibleItems = useMemo(() => filterVisible(selections, MAX_VISIBLE), [selections]);
  const numExtra = selections.length - visibleItems.length;

  const copySelectionText = () => {
    const text = selections.map((annotation) => annotation.text).join(' ');
    copy(text);
    trackDataEvent(DataEventActionsEnum.COPY, ViewerDataEventObject.ANNOTATION, {
      annotations: selectedAnnotationsForDataTracking,
      method: 'keyboard_shortcut',
    });
  };

  // In practice, this function is only accessed via the keyboard shortcut.
  // In developer mode, however, this function can also be used as a click handler.
  const onCopySelection = useEventHandler(() => {
    copySelectionText();
    setCanShowCopyButton(false);
  });

  const onMetaPress = useEventHandler(() => {
    if (selections.length !== 1) {
      return;
    }
    const selection = selections[0];
    if (selection.isMultiSelect) {
      return;
    }
    // In the case that there is a singular annotation selected (not in multiselect mode),
    // pressing the meta button moves the selection into multiselect mode.
    toggleSelection(selection);
    toggleSelection({...selection, isMultiSelect: true});
  });

  const keyHandlers = {
    [KeyboardActionsEnum.COPY]: onCopySelection,
    [KeyboardActionsEnum.CLOSE]: clearAllSelections,
    [KeyboardActionsEnum.META]: onMetaPress,
  };

  if (isEmpty(selections)) {
    return null;
  }

  const maybeRenderControls = () => {
    if (!shouldRenderControls) {
      return null;
    }
    return (
      <StyledWrapper>
        <FloatingToolbar size={VisualSizesEnum.LARGE}>
          <StyledItemsDiv>
            {visibleItems.map((item) => (
              <StyledItemSpan
                key={item.id}
                onClick={() =>
                  toggleSelection({
                    id: item.id,
                    text: item.text,
                    anchor: item.anchor,
                    isMultiSelect: item.isMultiSelect,
                    firstParentElement: item.firstParentElement,
                    secondParentElement: item.secondParentElement,
                  })
                }
              >
                {item.text}
              </StyledItemSpan>
            ))}
            {numExtra > 0 && <StyledItemSpan>+{numExtra}</StyledItemSpan>}
          </StyledItemsDiv>
          <StyledButtonsDiv>
            <FloatingToolbarButton
              iconName="copySmall"
              label={<Trans id="selection-shop-copy">Copy</Trans>}
              size={VisualSizesEnum.LARGE}
              onClick={onCopySelection}
              shouldRenderLabelInline
            />
          </StyledButtonsDiv>
        </FloatingToolbar>
      </StyledWrapper>
    );
  };

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      <LegacyInteractionParent interactionId="multi-selection">
        {maybeRenderControls()}
      </LegacyInteractionParent>
    </KeyboardShortcuts>
  );
};

// Only preview the first items to mitigate overflowing. Because items can have varying sizes, we cut based on the length of the content.
// Show at least 2 items, then cut once the content is too long.
function filterVisible(
  selections: ReadonlyArray<SelectionItem>,
  maxSize: number,
): ReadonlyArray<SelectionItem> {
  let size = 0;
  return selections.filter((item, index) => {
    size += Math.min(item.text.length, 20); // individual items have a max size.

    return index < 2 || size < maxSize;
  });
}
